// Catálogo das cores de destaque do Dashboard.
// As chaves antigas (iris, blue, green, amber, violet, ruby, cyan, lime,
// orange, red e slate) continuam válidas para preservar preferências salvas.
export const DEFAULT_PRIMARY_COLOR = 'blue';

export const PRIMARY_COLOR_FAMILIES = [
  'blues',
  'purples',
  'greens',
  'warm',
  'neutral',
];

export const PRIMARY_COLOR_OPTIONS = [
  { key: 'blue', hex: '#2781f6', family: 'blues' },
  { key: 'iris', hex: '#5b5bd6', family: 'blues' },
  { key: 'sky', hex: '#0277b8', family: 'blues', isNew: true },
  { key: 'ocean', hex: '#0369a1', family: 'blues', isNew: true },
  { key: 'cyan', hex: '#0891b2', family: 'blues' },
  { key: 'violet', hex: '#6e56cf', family: 'purples' },
  { key: 'purple', hex: '#9333ea', family: 'purples', isNew: true },
  { key: 'fuchsia', hex: '#c026d3', family: 'purples', isNew: true },
  { key: 'pink', hex: '#db2777', family: 'purples', isNew: true },
  { key: 'ruby', hex: '#ff265b', family: 'purples' },
  { key: 'green', hex: '#12a594', family: 'greens' },
  { key: 'teal', hex: '#0f766e', family: 'greens', isNew: true },
  { key: 'emerald', hex: '#047857', family: 'greens', isNew: true },
  { key: 'lime', hex: '#65a30d', family: 'greens' },
  { key: 'amber', hex: '#ffa31a', family: 'warm' },
  { key: 'gold', hex: '#a16207', family: 'warm', isNew: true },
  { key: 'orange', hex: '#f97316', family: 'warm' },
  { key: 'copper', hex: '#b45309', family: 'warm', isNew: true },
  { key: 'red', hex: '#dc2626', family: 'warm' },
  { key: 'crimson', hex: '#e11d48', family: 'warm', isNew: true },
  { key: 'slate', hex: '#475569', family: 'neutral' },
  { key: 'zinc', hex: '#66666f', family: 'neutral', isNew: true },
];

export const normalizePrimaryColor = color =>
  PRIMARY_COLOR_OPTIONS.some(option => option.key === color)
    ? color
    : DEFAULT_PRIMARY_COLOR;

export const findPrimaryColor = color =>
  PRIMARY_COLOR_OPTIONS.find(
    option => option.key === normalizePrimaryColor(color)
  );

const hexToRgb = hex => {
  const value = hex.replace('#', '');
  return [0, 2, 4].map(index => parseInt(value.slice(index, index + 2), 16));
};

const toLinear = channel => {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

export const relativeLuminance = hex => {
  const [red, green, blue] = hexToRgb(hex).map(toLinear);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

export const contrastRatio = (first, second) => {
  const [lighter, darker] = [
    relativeLuminance(first),
    relativeLuminance(second),
  ].sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
};

// O Dashboard usa texto branco sobre a cor de destaque (botões, badges).
export const WHITE_TEXT = '#ffffff';
export const DARK_TEXT = '#111827';
export const AA_NORMAL_TEXT = 4.5;

export const whiteTextContrast = hex => contrastRatio(hex, WHITE_TEXT);

export const meetsWhiteTextAA = hex => whiteTextContrast(hex) >= AA_NORMAL_TEXT;

// Cor de texto mais legível sobre a amostra (usada no check da seleção).
export const readableTextOn = hex =>
  contrastRatio(hex, WHITE_TEXT) >= contrastRatio(hex, DARK_TEXT)
    ? WHITE_TEXT
    : DARK_TEXT;

const mixChannel = (from, to, amount) =>
  Math.round(from + (to - from) * amount);

const mix = (hex, target, amount) => {
  const source = hexToRgb(hex);
  const destination = hexToRgb(target);
  return source.map((channel, index) =>
    mixChannel(channel, destination[index], amount)
  );
};

const asTriplet = channels => channels.join(' ');

// Gera a mesma escala de variáveis usada pelas cores de destaque existentes.
export const buildPrimaryColorVariables = hex => {
  const [red, green, blue] = hexToRgb(hex);
  const tint4 = asTriplet(mix(hex, '#ffffff', 0.82));
  return {
    '--blue-3': asTriplet(mix(hex, '#ffffff', 0.9)),
    '--blue-4': tint4,
    '--blue-5': asTriplet(mix(hex, '#ffffff', 0.73)),
    '--blue-8': asTriplet(mix(hex, '#ffffff', 0.35)),
    '--blue-9': asTriplet([red, green, blue]),
    '--blue-10': asTriplet(mix(hex, '#000000', 0.1)),
    '--blue-11': asTriplet(mix(hex, '#000000', 0.22)),
    '--solid-blue': tint4,
    '--border-blue': `${red}, ${green}, ${blue}, 0.5`,
  };
};
